import { useEffect, useRef } from 'react';
import { UseQueryResult } from '@tanstack/react-query';
import type { ReportStateProps } from '../components/ReportState';
import { Paged, ReportFilters } from '../types';

export type ReportStateBaseProps = Omit<ReportStateProps, 'children'>;

// Chưa có dữ liệu mà cũng chưa lỗi (vd. query đang tắt) vẫn tính là đang tải để không chớp thông báo rỗng
export const reportStateProps = (query: UseQueryResult<unknown>): ReportStateBaseProps => ({
  isLoading: query.isLoading || (!query.data && !query.isError),
  isError: query.isError,
  error: query.error,
  isEmpty: false,
  isRefreshing: query.isPlaceholderData,
  isFetching: query.isFetching,
  onRetry: () => query.refetch(),
});

// Trang đang xem vượt quá trang cuối (vd. dữ liệu ít đi sau khi mở link cũ): API trả rỗng nhưng total > 0
const isOutOfRange = (data?: Paged<unknown>) => !!data && data.items.length === 0 && data.total > 0 && data.page > 1;

// Khung chung của các bảng phân trang: trạng thái tải/lỗi/rỗng, trang hiện tại và tự về trang 1 khi vượt phạm vi
export function useReportList<T>(
  query: UseQueryResult<Paged<T>>,
  filters: ReportFilters,
  onPageChange: (page: number) => void
) {
  const { data } = query;
  const outOfRange = isOutOfRange(data);

  // Đọc qua ref: nơi gọi truyền arrow mới mỗi render, để vào deps sẽ gọi lặp khi dữ liệu cũ còn giữ
  const onPageChangeRef = useRef(onPageChange);
  onPageChangeRef.current = onPageChange;
  useEffect(() => {
    if (outOfRange) onPageChangeRef.current(1);
  }, [data, outOfRange]);

  const base = reportStateProps(query);
  const stateProps: ReportStateBaseProps = {
    ...base,
    // Đang về trang 1: hiện skeleton thay vì báo "không có lượt kích hoạt" sai
    isLoading: base.isLoading || outOfRange,
    isEmpty: !!data && data.items.length === 0 && data.total === 0,
  };

  return {
    data,
    page: data?.page ?? filters.page ?? 1,
    perPage: data?.per_page ?? filters.per_page ?? 20,
    stateProps,
  };
}
